import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { RowModel } from "@tanstack/react-table";

import useEbayAspects from "../../data/useEbayAspects";
import usePartAspectsMutations from "../../data/usePartAspectsMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IEbayAspect, IPartAspect } from "../../models/EbayOffer";
import { IPart } from "../../models/Part";
import toTitleCase from "../../utils/toTitleCase";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import ComboBoxMultipleInput from "../shared/Inputs/ComboBoxMultipleInput";
import LoadingWheel from "../shared/LoadingWheel";
import { TextInput } from "../shared/Inputs/TextInput";

interface BulkEditListingSpecificsProps {
  parts: IPart[];
  onClose: () => void;
  // When false, aspects are only saved locally (no live eBay revise) - they flow
  // onto the new listing when the part is relisted.
  updateOffer?: boolean;
}

export default function BulkEditListingSpecifics({ parts, onClose, updateOffer = true }: BulkEditListingSpecificsProps) {
  const { bulkUpdate } = usePartAspectsMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Aspects");
  const { register, handleSubmit, getValues, setValue } = useForm({});
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get unique category IDs from parts (excluding empty strings and "0")
  const categoryIds = useMemo(() => {
    return Array.from(new Set(
      parts
        .map(p => p.offers?.[0]?.categoryId)
        .filter((id): id is string => !!id && id !== "0" && id.trim() !== "")
    ));
  }, [parts]);

  // Get the first category ID for eBay aspects (all should be the same)
  const categoryId = categoryIds[0];

  // Check if all parts have the same category ID and it's valid (not empty or "0")
  const hasSameCategory = categoryIds.length === 1 && !!categoryId && categoryId !== "0";

  const { aspects: ebayAspects } = useEbayAspects(categoryId || "");

  useEffect(() => {
    if (!ebayAspects.isLoading) {
      setIsLoading(false);
    }
  }, [ebayAspects.isLoading]);

  const onSubmit = (data: any) => {
    setError(null);

    // Validate category IDs for eBay aspects
    const ebayAspectNames = Object.keys(data).filter(key => {
      if (key === "newAspectName" || key === "newAspectValue") return false;
      // Check if this is an eBay aspect (not custom)
      return ebayAspects.data?.some(aspect => aspect.localizedAspectName === key);
    });

    // Validate category IDs for eBay aspects
    if (ebayAspectNames.length > 0) {
      // Check if all parts have valid category IDs
      const allCategoryIds = parts
        .map(p => p.offers?.[0]?.categoryId)
        .filter((id): id is string => !!id);

      if (allCategoryIds.length === 0) {
        setError("Cannot update eBay aspects: Selected parts do not have CategoryIds set.");
        return;
      }

      // Check for empty or "0" category IDs
      const invalidCategoryIds = allCategoryIds.filter(id => !id || id === "0" || id.trim() === "");
      if (invalidCategoryIds.length > 0) {
        setError("Cannot update eBay aspects: Some selected parts have invalid CategoryIds (empty or 0). All parts must have valid CategoryIds to update eBay aspects.");
        return;
      }

      // Check if all parts have the same category ID
      const uniqueCategoryIds = Array.from(new Set(allCategoryIds));
      if (uniqueCategoryIds.length > 1) {
        setError("Cannot update eBay aspects: Selected parts have different CategoryIds. All parts must have the same CategoryId to update eBay aspects.");
        return;
      }

      // Final check that the category ID is valid
      if (!categoryId || categoryId === "0" || categoryId.trim() === "") {
        setError("Cannot update eBay aspects: CategoryId is invalid (empty or 0).");
        return;
      }
    }

    // Build aspects array
    // Note: We only include aspects that have values. Blank/empty fields are skipped,
    // so existing values won't be overwritten if a field is left blank.
    const aspects: any[] = [];

    // Process form data
    Object.keys(data).forEach((key) => {
      // Skip empty values and form helper fields - this ensures blank fields don't overwrite existing values
      if (data[key] === "" || key === "newAspectName" || key === "newAspectValue") {
        return;
      }

      const isEbayAspect = ebayAspects.data?.some(aspect => aspect.localizedAspectName === key);

      aspects.push({
        name: key,
        value: data[key]?.localizedValue ?? data[key], // If it's a combo box, get the localized value
        isCustom: !isEbayAspect,
      });
    });

    if (aspects.length === 0) {
      setError("Please fill in at least one aspect");
      return;
    }

    const partIds = parts.map(p => p.id!);

    bulkUpdate.mutate(
      {
        body: {
          partIds,
          aspects
        },
        updateOffer
      },
      {
        onSuccess: (response: any) => {
          // Handle both PascalCase and camelCase response formats
          const message = response?.Message ?? response?.message;

          addNotification({
            variant: "success",
            primaryText: message || "Successfully updated aspects",
          });

          saveHandlers.onSuccess();
          onClose();
        },
        onError: (error: any) => {
          const errorMessage = error?.message || error?.Message || "Failed to update aspects";
          setError(errorMessage);
          saveHandlers.onError();
        }
      }
    );
  };

  const sortByRequired = (a: IEbayAspect, b: IEbayAspect) => {
    if (a.aspectConstraint.aspectRequired === b.aspectConstraint.aspectRequired) {
      return 0;
    }

    if (a.aspectConstraint.aspectRequired) {
      return -1;
    }

    return 1;
  };

  if (isLoading) {
    return <LoadingWheel />;
  }

  return (
    <div>
      <h1 className="text-lg font-semibold">Bulk Update Item Specifics</h1>
      <h2 className="text-sm text-gray-600"> Updating aspects for {parts.length} part(s)</h2>
      <h2 className={`text-sm text-gray-600 ${updateOffer ? "mb-4" : ""}`}> Leaving a field blank will not update that aspect</h2>
      {!updateOffer && (
        <h2 className="text-sm text-gray-600 mb-4"> Aspects are saved locally only - the live eBay listing is not revised. They are applied when the listing is relisted.</h2>
      )}

      {!hasSameCategory && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> Selected parts have different eBay categories. Only custom aspects can be updated. eBay aspects require all parts to have the same ebay category.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-2 space-y-2">
        {/* Default Standard Aspects Section */}
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold mb-1">Standard Default Aspects</h2>
          <div className="border border-gray-200 rounded">
            {/* Custom Default Aspects */}
            {["Grade", "Tag Number", "Type", "Other Part Numbers"].map((aspectName, index) => {
              const isEven = index % 2 === 0;
              const baseBg = isEven ? 'bg-gray-100' : 'bg-gray-200';

              return (
                <div key={aspectName} className={`flex items-center border-b border-gray-100 last:border-b-0 ${baseBg} transition-colors`}>
                  <div className="w-1/3 px-3 py-2 text-sm font-medium text-gray-700 border-r border-gray-300">
                    {toTitleCase(aspectName)}
                  </div>
                  <div className="flex-1 px-3 py-2">
                    <TextInput
                      size="sm"
                      label=""
                      register={register}
                      registerName={aspectName}
                      maxLength={50}
                    />
                  </div>
                </div>
              );
            })}
            {/* eBay Default Aspects - Only show if all parts have same category */}
            {hasSameCategory && categoryId && ["Brand", "Manufacturer Part Number", "Superseded Part Number"].map((aspectName, index) => {
              const ebayAspect = ebayAspects.data?.find(a => a.localizedAspectName === aspectName);
              const isEven = (index + 4) % 2 === 0;
              const baseBg = isEven ? 'bg-gray-100' : 'bg-gray-200';

              if (ebayAspect && ebayAspect.aspectValues && ebayAspect.aspectValues.length > 0) {
                // Use ComboBoxInput for eBay aspects with predefined values
                return (
                  <div key={aspectName} className={`flex items-center border-b border-gray-100 last:border-b-0 ${baseBg} transition-colors`}>
                    <div className="w-1/3 px-3 py-2 text-sm font-medium text-gray-700 border-r border-gray-300">
                      {toTitleCase(aspectName)}
                    </div>
                    <div className="flex-1 px-3 py-2">
                      <ComboBoxInput
                        size="sm"
                        options={ebayAspect.aspectValues}
                        searchField={"localizedValue"}
                        register={register}
                        setValue={setValue}
                        registerName={aspectName}
                      />
                    </div>
                  </div>
                );
              } else {
                // Use TextInput for free-form values
                return (
                  <div key={aspectName} className={`flex items-center border-b border-gray-100 last:border-b-0 ${baseBg} transition-colors`}>
                    <div className="w-1/3 px-3 py-2 text-sm font-medium text-gray-700 border-r border-gray-300">
                      {toTitleCase(aspectName)}
                    </div>
                    <div className="flex-1 px-3 py-2">
                      <TextInput
                        size="sm"
                        label=""
                        register={register}
                        registerName={aspectName}
                        maxLength={50}
                      />
                    </div>
                  </div>
                );
              }
            })}
          </div>
        </div>

        {/* eBay Aspects Section - Only show if all parts have same category */}
        {hasSameCategory && categoryId && (
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-semibold mb-1">eBay Aspects</h2>
            <div className="border border-gray-200 rounded">
              {ebayAspects.data?.sort((a: IEbayAspect, b: IEbayAspect) => sortByRequired(a, b)).map((aspect: IEbayAspect, index: number) => {
                const isRequired = aspect.aspectConstraint.aspectRequired;
                const label = `${aspect.localizedAspectName}${isRequired ? " *" : ""}`;
                const isEven = index % 2 === 0;
                const baseBg = isEven ? 'bg-gray-100' : 'bg-gray-200';

                if (!aspect.aspectValues || aspect.aspectValues.length === 0) {
                  return (
                    <div key={aspect.localizedAspectName} className={`flex items-center border-b border-gray-100 last:border-b-0 ${baseBg} transition-colors`}>
                      <div className="w-1/3 px-3 py-2 text-sm font-medium text-gray-700 border-r border-gray-300">
                        {toTitleCase(label)}
                      </div>
                      <div className="flex-1 px-3 py-2">
                        <TextInput
                          size="sm"
                          label=""
                          register={register}
                          registerName={aspect.localizedAspectName}
                          maxLength={50}
                        />
                      </div>
                    </div>
                  );
                }

                if (aspect.aspectConstraint.itemToAspectCardinality === "MULTI") {
                  return (
                    <div key={aspect.localizedAspectName} className={`flex items-center border-b border-gray-100 last:border-b-0 ${baseBg} transition-colors`}>
                      <div className="w-1/3 px-3 py-2 text-sm font-medium text-gray-700 border-r border-gray-300">
                        {toTitleCase(label)} (Multi)
                      </div>
                      <div className="flex-1 px-3 py-2">
                        <ComboBoxMultipleInput
                          size="sm"
                          options={aspect.aspectValues}
                          searchField={"localizedValue"}
                          register={register}
                          setValue={setValue}
                          registerName={aspect.localizedAspectName}
                        />
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div key={aspect.localizedAspectName} className={`flex items-center border-b border-gray-100 last:border-b-0 ${baseBg} transition-colors`}>
                      <div className="w-1/3 px-3 py-2 text-sm font-medium text-gray-700 border-r border-gray-300">
                        {toTitleCase(label)}
                      </div>
                      <div className="flex-1 px-3 py-2">
                        <ComboBoxInput
                          size="sm"
                          options={aspect.aspectValues}
                          searchField={"localizedValue"}
                          register={register}
                          setValue={setValue}
                          registerName={aspect.localizedAspectName}
                        />
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        )}

        {/* Custom Aspects Section */}
        <div className="flex flex-col gap-1 mt-4">
          <h2 className="text-lg font-semibold mb-1">Custom Aspects</h2>
          <div className="border border-gray-200 rounded p-3">
            <div className="grid grid-cols-2 gap-3">
              <TextInput label={"Name: (Max: 40)"} register={register} registerName={"newAspectName"} maxLength={40} size="sm" />
              <TextInput label={"Value: (Max: 50)"} register={register} registerName={"newAspectValue"} maxLength={50} size="sm" />
            </div>
            <div className="mt-2">
              <ButtonInput
                label={"Add Custom Aspect"}
                isSubmit={false}
                onClick={() => {
                  const name = getValues().newAspectName;
                  const value = getValues().newAspectValue;
                  if (name && value) {
                    setValue(name, value);
                    setValue("newAspectName", "");
                    setValue("newAspectValue", "");
                  }
                }}
                classes={undefined}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <ButtonInput
            label={"Cancel"}
            isSubmit={false}
            onClick={onClose}
            classes={"!bg-white !border-1 !border-gray-400 !text-gray-600 hover:!bg-gray-200"}
          />

          <ButtonInput
            label={"Save"}
            isSubmit={true}
            onClick={undefined}
            classes={undefined}
            isLoading={bulkUpdate.isLoading}
          />
        </div>
      </form>
    </div>
  );
}

